import { defineDomainTranslations } from './defineDomainTranslations';

const en = {
  'recordingEditor.aborted.description': 'This recording was aborted and cannot be processed.',
  'recordingEditor.aborted.title': 'Recording aborted',
  'recordingEditor.analyzing.title': 'Analyzing your speech patterns...',
  'recordingEditor.autoZoom.cardTitle': 'Zoom {number}',
  'recordingEditor.autoZoom.emptyDescription': "We didn't detect any moment worth zooming into in this recording.",
  'recordingEditor.autoZoom.emptyTitle': 'No zooms',
  'recordingEditor.autoZoom.locked': 'Zooms in on what you click so viewers can follow.',
  'recordingEditor.autoZoom.masterSwitch': 'Turn all auto zooms on / off',
  'recordingEditor.autoZoom.preset.medium': 'Medium',
  'recordingEditor.autoZoom.preset.strong': 'Strong',
  'recordingEditor.autoZoom.preset.subtle': 'Subtle',
  'recordingEditor.autoZoom.title': 'Auto zoom',
  'recordingEditor.autoZoom.turnOff': 'Turn off zoom starting at {time}',
  'recordingEditor.autoZoom.turnOn': 'Turn on zoom starting at {time}',
  'recordingEditor.autoZoom.zoomSwitchTooltip': 'Enable / disable this zoom',
  'recordingEditor.defaultTitle': 'Recording',
  'recordingEditor.failed.description': 'Processing failed. Please try again or come back later.',
  'recordingEditor.failed.overlay': 'Failed to process',
  'recordingEditor.failed.title': 'Processing failed',
  'recordingEditor.failed.tryAgain': 'Try again',
  'recordingEditor.header.back': 'Back to recordings',
  'recordingEditor.header.download': 'Download',
  'recordingEditor.header.downloading': 'Downloading...',
  'recordingEditor.header.viewPlans': 'View plans',
  'recordingEditor.leaveHint': 'Feel free to leave this page and come back later.',
  'recordingEditor.mobileBanner.dismiss': 'Dismiss',
  'recordingEditor.mobileBanner.text': 'For the best editing experience, open on desktop.',
  'recordingEditor.notFound.description': "The page you are looking for doesn't exist.",
  'recordingEditor.notFound.goHome': 'Go home',
  'recordingEditor.notFound.title': 'Page not found',
  'recordingEditor.player.back': 'Back 10 seconds',
  'recordingEditor.player.backTooltip': 'Back 10 sec',
  'recordingEditor.player.forward': 'Forward 10 seconds',
  'recordingEditor.player.forwardTooltip': 'Forward 10 sec',
  'recordingEditor.player.pause': 'Pause',
  'recordingEditor.player.play': 'Play',
  'recordingEditor.player.playPauseTooltip': 'Play / Pause',
  'recordingEditor.player.seek': 'Playback position',
  'recordingEditor.player.spaceKey': 'Space',
  'recordingEditor.player.speed': 'Adjust playback speed',
  'recordingEditor.player.speedTooltip': 'Adjust speed',
  'recordingEditor.player.translate': 'Translate',
  'recordingEditor.player.translateTooltip': 'Localize this video in the translation editor',
  'recordingEditor.preview.description': 'Preview of your polished video. Final rendering is in progress.',
  'recordingEditor.preview.preparing': 'Preparing preview...',
  'recordingEditor.processing.title': 'Processing video',
  'recordingEditor.rendering.description':
    "We're applying your latest edits. You can keep watching below in the meantime - just keep this tab open.",
  'recordingEditor.rendering.downloadStarted': 'Download started',
  'recordingEditor.rendering.locked': 'Editing locked while rendering',
  'recordingEditor.rendering.shareDescription':
    "Your share link will open automatically when it's ready. You can keep watching below — just don't close this tab.",
  'recordingEditor.rendering.title': 'Getting your video ready…',
  'recordingEditor.timeline.autoPauseOff': 'Turn off auto pause cut starting at {time}',
  'recordingEditor.timeline.autoPauseOn': 'Turn on auto pause cut starting at {time}',
  'recordingEditor.timeline.autoPauseRemove': 'Remove automatic pause cut at {time}',
  'recordingEditor.timeline.autoPauseRestore': 'Restore automatic pause cut at {time}',
  'recordingEditor.timeline.frameAlt': 'Frame at {time}',
  'recordingEditor.timeline.moveZoom': 'Move zoom starting at {time}',
  'recordingEditor.timeline.removed': 'Removed',
  'recordingEditor.timeline.zoomChip': 'zoom',
  'recordingEditor.timeline.zoomIn': 'Zoom timeline in',
  'recordingEditor.timeline.zoomOut': 'Zoom timeline out',
  'recordingEditor.timeline.zoomSlider': 'Timeline zoom',
  'recordingEditor.transcript.cutHint': 'Word removed from video. Click it to undo.',
  'recordingEditor.transcript.emptyDescription':
    'Nothing to cut here — you can still preview, share or download the recording.',
  'recordingEditor.transcript.emptyTitle': 'No speech detected',
  'recordingEditor.transcript.hint': 'Tap words to remove or restore them in your video.',
  'recordingEditor.transcript.tabTooltip': 'Edit your video by editing its transcript.',
  'recordingEditor.transcript.title': 'Transcript',
} as const;

type Translation = Record<keyof typeof en, string>;

const es = {
  'recordingEditor.aborted.description': 'Esta grabación se interrumpió y no se puede procesar.',
  'recordingEditor.aborted.title': 'Grabación interrumpida',
  'recordingEditor.analyzing.title': 'Analizando tu forma de hablar...',
  'recordingEditor.autoZoom.cardTitle': 'Zoom {number}',
  'recordingEditor.autoZoom.emptyDescription': 'No detectamos ningún momento de esta grabación que merezca un zoom.',
  'recordingEditor.autoZoom.emptyTitle': 'Sin zooms',
  'recordingEditor.autoZoom.locked':
    'Amplía la zona en la que haces clic para que los espectadores puedan seguir lo que ocurre.',
  'recordingEditor.autoZoom.masterSwitch': 'Activar o desactivar todos los zooms automáticos',
  'recordingEditor.autoZoom.preset.medium': 'Medio',
  'recordingEditor.autoZoom.preset.strong': 'Fuerte',
  'recordingEditor.autoZoom.preset.subtle': 'Sutil',
  'recordingEditor.autoZoom.title': 'Zoom automático',
  'recordingEditor.autoZoom.turnOff': 'Desactivar el zoom que empieza en {time}',
  'recordingEditor.autoZoom.turnOn': 'Activar el zoom que empieza en {time}',
  'recordingEditor.autoZoom.zoomSwitchTooltip': 'Activar / desactivar este zoom',
  'recordingEditor.defaultTitle': 'Grabación',
  'recordingEditor.failed.description': 'No se pudo procesar. Vuelve a intentarlo o regresa más tarde.',
  'recordingEditor.failed.overlay': 'No se pudo procesar',
  'recordingEditor.failed.title': 'Error al procesar',
  'recordingEditor.failed.tryAgain': 'Intentar de nuevo',
  'recordingEditor.header.back': 'Volver a las grabaciones',
  'recordingEditor.header.download': 'Descargar',
  'recordingEditor.header.downloading': 'Descargando...',
  'recordingEditor.header.viewPlans': 'Ver planes',
  'recordingEditor.leaveHint': 'Puedes salir de esta página y volver más tarde.',
  'recordingEditor.mobileBanner.dismiss': 'Cerrar',
  'recordingEditor.mobileBanner.text': 'Para una mejor experiencia de edición, abre la grabación en un ordenador.',
  'recordingEditor.notFound.description': 'La página que buscas no existe.',
  'recordingEditor.notFound.goHome': 'Ir al inicio',
  'recordingEditor.notFound.title': 'Página no encontrada',
  'recordingEditor.player.back': 'Retroceder 10 segundos',
  'recordingEditor.player.backTooltip': 'Retroceder 10 s',
  'recordingEditor.player.forward': 'Avanzar 10 segundos',
  'recordingEditor.player.forwardTooltip': 'Avanzar 10 s',
  'recordingEditor.player.pause': 'Pausar',
  'recordingEditor.player.play': 'Reproducir',
  'recordingEditor.player.playPauseTooltip': 'Reproducir / Pausar',
  'recordingEditor.player.seek': 'Posición de reproducción',
  'recordingEditor.player.spaceKey': 'Espacio',
  'recordingEditor.player.speed': 'Ajustar la velocidad de reproducción',
  'recordingEditor.player.speedTooltip': 'Ajustar velocidad',
  'recordingEditor.player.translate': 'Traducir',
  'recordingEditor.player.translateTooltip': 'Localiza este video en el editor de traducción',
  'recordingEditor.preview.description': 'Vista previa de tu video mejorado. El renderizado final está en curso.',
  'recordingEditor.preview.preparing': 'Preparando la vista previa...',
  'recordingEditor.processing.title': 'Procesando video',
  'recordingEditor.rendering.description':
    'Estamos aplicando tus últimos cambios. Mientras tanto, puedes seguir viendo el video abajo; solo mantén esta pestaña abierta.',
  'recordingEditor.rendering.downloadStarted': 'Descarga iniciada',
  'recordingEditor.rendering.locked': 'Edición bloqueada durante el renderizado',
  'recordingEditor.rendering.shareDescription':
    'El enlace para compartir se abrirá automáticamente cuando esté listo. Puedes seguir viendo el video abajo, pero no cierres esta pestaña.',
  'recordingEditor.rendering.title': 'Preparando tu video…',
  'recordingEditor.timeline.autoPauseOff': 'Desactivar el corte automático de pausa que empieza en {time}',
  'recordingEditor.timeline.autoPauseOn': 'Activar el corte automático de pausa que empieza en {time}',
  'recordingEditor.timeline.autoPauseRemove': 'Quitar el corte automático de pausa en {time}',
  'recordingEditor.timeline.autoPauseRestore': 'Restaurar el corte automático de pausa en {time}',
  'recordingEditor.timeline.frameAlt': 'Fotograma en {time}',
  'recordingEditor.timeline.moveZoom': 'Mover el zoom que empieza en {time}',
  'recordingEditor.timeline.removed': 'Eliminado',
  'recordingEditor.timeline.zoomChip': 'zoom',
  'recordingEditor.timeline.zoomIn': 'Acercar la línea de tiempo',
  'recordingEditor.timeline.zoomOut': 'Alejar la línea de tiempo',
  'recordingEditor.timeline.zoomSlider': 'Zoom de la línea de tiempo',
  'recordingEditor.transcript.cutHint': 'Palabra eliminada del video. Haz clic en ella para deshacer.',
  'recordingEditor.transcript.emptyDescription':
    'No hay nada que recortar, pero aún puedes previsualizar, compartir o descargar la grabación.',
  'recordingEditor.transcript.emptyTitle': 'No se detectó voz',
  'recordingEditor.transcript.hint': 'Toca las palabras para quitarlas del video o recuperarlas.',
  'recordingEditor.transcript.tabTooltip': 'Edita tu video modificando la transcripción.',
  'recordingEditor.transcript.title': 'Transcripción',
} satisfies Translation;

const pt = {
  'recordingEditor.aborted.description': 'Esta gravação foi cancelada e não pode ser processada.',
  'recordingEditor.aborted.title': 'Gravação cancelada',
  'recordingEditor.analyzing.title': 'Analisando seu jeito de falar...',
  'recordingEditor.autoZoom.cardTitle': 'Zoom {number}',
  'recordingEditor.autoZoom.emptyDescription': 'Não encontramos nenhum momento nesta gravação que valha um zoom.',
  'recordingEditor.autoZoom.emptyTitle': 'Nenhum zoom',
  'recordingEditor.autoZoom.locked': 'Dá zoom onde você clica para que os espectadores acompanhem.',
  'recordingEditor.autoZoom.masterSwitch': 'Ativar ou desativar todos os zooms automáticos',
  'recordingEditor.autoZoom.preset.medium': 'Médio',
  'recordingEditor.autoZoom.preset.strong': 'Forte',
  'recordingEditor.autoZoom.preset.subtle': 'Sutil',
  'recordingEditor.autoZoom.title': 'Zoom automático',
  'recordingEditor.autoZoom.turnOff': 'Desativar o zoom que começa em {time}',
  'recordingEditor.autoZoom.turnOn': 'Ativar o zoom que começa em {time}',
  'recordingEditor.autoZoom.zoomSwitchTooltip': 'Ativar / desativar este zoom',
  'recordingEditor.defaultTitle': 'Gravação',
  'recordingEditor.failed.description': 'Falha no processamento. Tente novamente ou volte mais tarde.',
  'recordingEditor.failed.overlay': 'Não foi possível processar',
  'recordingEditor.failed.title': 'Falha no processamento',
  'recordingEditor.failed.tryAgain': 'Tentar novamente',
  'recordingEditor.header.back': 'Voltar para as gravações',
  'recordingEditor.header.download': 'Baixar',
  'recordingEditor.header.downloading': 'Baixando...',
  'recordingEditor.header.viewPlans': 'Ver planos',
  'recordingEditor.leaveHint': 'Você pode sair desta página e voltar mais tarde.',
  'recordingEditor.mobileBanner.dismiss': 'Fechar',
  'recordingEditor.mobileBanner.text': 'Para uma experiência de edição melhor, abra no computador.',
  'recordingEditor.notFound.description': 'A página que você procura não existe.',
  'recordingEditor.notFound.goHome': 'Ir para o início',
  'recordingEditor.notFound.title': 'Página não encontrada',
  'recordingEditor.player.back': 'Voltar 10 segundos',
  'recordingEditor.player.backTooltip': 'Voltar 10 s',
  'recordingEditor.player.forward': 'Avançar 10 segundos',
  'recordingEditor.player.forwardTooltip': 'Avançar 10 s',
  'recordingEditor.player.pause': 'Pausar',
  'recordingEditor.player.play': 'Reproduzir',
  'recordingEditor.player.playPauseTooltip': 'Reproduzir / Pausar',
  'recordingEditor.player.seek': 'Posição da reprodução',
  'recordingEditor.player.spaceKey': 'Espaço',
  'recordingEditor.player.speed': 'Ajustar a velocidade de reprodução',
  'recordingEditor.player.speedTooltip': 'Ajustar velocidade',
  'recordingEditor.player.translate': 'Traduzir',
  'recordingEditor.player.translateTooltip': 'Adapte este vídeo no editor de tradução',
  'recordingEditor.preview.description': 'Prévia do seu vídeo finalizado. A renderização final está em andamento.',
  'recordingEditor.preview.preparing': 'Preparando a prévia...',
  'recordingEditor.processing.title': 'Processando vídeo',
  'recordingEditor.rendering.description':
    'Estamos aplicando suas últimas edições. Enquanto isso, você pode continuar assistindo abaixo, só mantenha esta aba aberta.',
  'recordingEditor.rendering.downloadStarted': 'Download iniciado',
  'recordingEditor.rendering.locked': 'Edição bloqueada durante a renderização',
  'recordingEditor.rendering.shareDescription':
    'O link de compartilhamento vai abrir automaticamente quando estiver pronto. Você pode continuar assistindo abaixo, só não feche esta aba.',
  'recordingEditor.rendering.title': 'Preparando seu vídeo…',
  'recordingEditor.timeline.autoPauseOff': 'Desativar o corte automático de pausa que começa em {time}',
  'recordingEditor.timeline.autoPauseOn': 'Ativar o corte automático de pausa que começa em {time}',
  'recordingEditor.timeline.autoPauseRemove': 'Remover o corte automático de pausa em {time}',
  'recordingEditor.timeline.autoPauseRestore': 'Restaurar o corte automático de pausa em {time}',
  'recordingEditor.timeline.frameAlt': 'Quadro em {time}',
  'recordingEditor.timeline.moveZoom': 'Mover o zoom que começa em {time}',
  'recordingEditor.timeline.removed': 'Removido',
  'recordingEditor.timeline.zoomChip': 'zoom',
  'recordingEditor.timeline.zoomIn': 'Ampliar a linha do tempo',
  'recordingEditor.timeline.zoomOut': 'Reduzir a linha do tempo',
  'recordingEditor.timeline.zoomSlider': 'Zoom da linha do tempo',
  'recordingEditor.transcript.cutHint': 'Palavra removida do vídeo. Clique nela para desfazer.',
  'recordingEditor.transcript.emptyDescription':
    'Não há nada para cortar, mas você ainda pode visualizar, compartilhar ou baixar a gravação.',
  'recordingEditor.transcript.emptyTitle': 'Nenhuma fala detectada',
  'recordingEditor.transcript.hint': 'Toque nas palavras para removê-las do vídeo ou restaurá-las.',
  'recordingEditor.transcript.tabTooltip': 'Edite seu vídeo pela transcrição.',
  'recordingEditor.transcript.title': 'Transcrição',
} satisfies Translation;

const fr = {
  'recordingEditor.aborted.description': 'Cet enregistrement a été interrompu et ne peut pas être traité.',
  'recordingEditor.aborted.title': 'Enregistrement interrompu',
  'recordingEditor.analyzing.title': 'Analyse de votre élocution...',
  'recordingEditor.autoZoom.cardTitle': 'Zoom {number}',
  'recordingEditor.autoZoom.emptyDescription':
    'Nous n’avons détecté aucun moment justifiant un zoom dans cet enregistrement.',
  'recordingEditor.autoZoom.emptyTitle': 'Aucun zoom',
  'recordingEditor.autoZoom.locked': 'Agrandit la zone sur laquelle vous cliquez pour aider les spectateurs à suivre.',
  'recordingEditor.autoZoom.masterSwitch': 'Activer ou désactiver tous les zooms automatiques',
  'recordingEditor.autoZoom.preset.medium': 'Moyen',
  'recordingEditor.autoZoom.preset.strong': 'Fort',
  'recordingEditor.autoZoom.preset.subtle': 'Léger',
  'recordingEditor.autoZoom.title': 'Zoom auto',
  'recordingEditor.autoZoom.turnOff': 'Désactiver le zoom qui commence à {time}',
  'recordingEditor.autoZoom.turnOn': 'Activer le zoom qui commence à {time}',
  'recordingEditor.autoZoom.zoomSwitchTooltip': 'Activer / désactiver ce zoom',
  'recordingEditor.defaultTitle': 'Enregistrement',
  'recordingEditor.failed.description': 'Le traitement a échoué. Réessayez ou revenez plus tard.',
  'recordingEditor.failed.overlay': 'Traitement impossible',
  'recordingEditor.failed.title': 'Échec du traitement',
  'recordingEditor.failed.tryAgain': 'Réessayer',
  'recordingEditor.header.back': 'Retour aux enregistrements',
  'recordingEditor.header.download': 'Télécharger',
  'recordingEditor.header.downloading': 'Téléchargement...',
  'recordingEditor.header.viewPlans': 'Voir les offres',
  'recordingEditor.leaveHint': 'Vous pouvez quitter cette page et revenir plus tard.',
  'recordingEditor.mobileBanner.dismiss': 'Fermer',
  'recordingEditor.mobileBanner.text':
    "Pour une meilleure expérience d'édition, ouvrez l'enregistrement sur un ordinateur.",
  'recordingEditor.notFound.description': 'La page que vous recherchez n’existe pas.',
  'recordingEditor.notFound.goHome': 'Retour à l’accueil',
  'recordingEditor.notFound.title': 'Page introuvable',
  'recordingEditor.player.back': 'Reculer de 10 secondes',
  'recordingEditor.player.backTooltip': 'Reculer de 10 s',
  'recordingEditor.player.forward': 'Avancer de 10 secondes',
  'recordingEditor.player.forwardTooltip': 'Avancer de 10 s',
  'recordingEditor.player.pause': 'Pause',
  'recordingEditor.player.play': 'Lecture',
  'recordingEditor.player.playPauseTooltip': 'Lecture / Pause',
  'recordingEditor.player.seek': 'Position de lecture',
  'recordingEditor.player.spaceKey': 'Espace',
  'recordingEditor.player.speed': 'Régler la vitesse de lecture',
  'recordingEditor.player.speedTooltip': 'Régler la vitesse',
  'recordingEditor.player.translate': 'Traduire',
  'recordingEditor.player.translateTooltip': 'Adapter cette vidéo dans l’éditeur de traduction',
  'recordingEditor.preview.description': 'Aperçu de votre vidéo finalisée. Le rendu final est en cours.',
  'recordingEditor.preview.preparing': 'Préparation de l’aperçu...',
  'recordingEditor.processing.title': 'Traitement de la vidéo',
  'recordingEditor.rendering.description':
    'Nous appliquons vos dernières modifications. En attendant, vous pouvez continuer à regarder la vidéo ci-dessous ; gardez simplement cet onglet ouvert.',
  'recordingEditor.rendering.downloadStarted': 'Téléchargement lancé',
  'recordingEditor.rendering.locked': 'Édition verrouillée pendant le rendu',
  'recordingEditor.rendering.shareDescription':
    'Le lien de partage s’ouvrira automatiquement une fois la vidéo prête. Vous pouvez continuer à regarder la vidéo ci-dessous, mais ne fermez pas cet onglet.',
  'recordingEditor.rendering.title': 'Préparation de votre vidéo…',
  'recordingEditor.timeline.autoPauseOff': 'Désactiver la coupe automatique de pause qui commence à {time}',
  'recordingEditor.timeline.autoPauseOn': 'Activer la coupe automatique de pause qui commence à {time}',
  'recordingEditor.timeline.autoPauseRemove': 'Supprimer la coupe automatique de pause à {time}',
  'recordingEditor.timeline.autoPauseRestore': 'Rétablir la coupe automatique de pause à {time}',
  'recordingEditor.timeline.frameAlt': 'Image à {time}',
  'recordingEditor.timeline.moveZoom': 'Déplacer le zoom qui commence à {time}',
  'recordingEditor.timeline.removed': 'Supprimé',
  'recordingEditor.timeline.zoomChip': 'zoom',
  'recordingEditor.timeline.zoomIn': 'Agrandir la timeline',
  'recordingEditor.timeline.zoomOut': 'Réduire la timeline',
  'recordingEditor.timeline.zoomSlider': 'Zoom de la timeline',
  'recordingEditor.transcript.cutHint': 'Mot supprimé de la vidéo. Cliquez dessus pour annuler.',
  'recordingEditor.transcript.emptyDescription':
    "Rien à couper ici — vous pouvez tout de même prévisualiser, partager ou télécharger l'enregistrement.",
  'recordingEditor.transcript.emptyTitle': 'Aucune parole détectée',
  'recordingEditor.transcript.hint': 'Cliquez sur les mots pour les retirer de votre vidéo ou les rétablir.',
  'recordingEditor.transcript.tabTooltip': 'Modifiez la transcription pour éditer votre vidéo.',
  'recordingEditor.transcript.title': 'Transcription',
} satisfies Translation;

const it = {
  'recordingEditor.aborted.description': 'Questa registrazione è stata interrotta e non può essere elaborata.',
  'recordingEditor.aborted.title': 'Registrazione interrotta',
  'recordingEditor.analyzing.title': 'Analisi del tuo modo di parlare...',
  'recordingEditor.autoZoom.cardTitle': 'Zoom {number}',
  'recordingEditor.autoZoom.emptyDescription': 'Non abbiamo rilevato momenti da ingrandire in questa registrazione.',
  'recordingEditor.autoZoom.emptyTitle': 'Nessuno zoom',
  'recordingEditor.autoZoom.locked': 'Fa lo zoom su ciò che clicchi, così chi guarda non perde il filo.',
  'recordingEditor.autoZoom.masterSwitch': 'Attiva o disattiva tutti gli zoom automatici',
  'recordingEditor.autoZoom.preset.medium': 'Medio',
  'recordingEditor.autoZoom.preset.strong': 'Forte',
  'recordingEditor.autoZoom.preset.subtle': 'Leggero',
  'recordingEditor.autoZoom.title': 'Zoom automatico',
  'recordingEditor.autoZoom.turnOff': 'Disattiva lo zoom che inizia a {time}',
  'recordingEditor.autoZoom.turnOn': 'Attiva lo zoom che inizia a {time}',
  'recordingEditor.autoZoom.zoomSwitchTooltip': 'Attiva / disattiva questo zoom',
  'recordingEditor.defaultTitle': 'Registrazione',
  'recordingEditor.failed.description': 'Elaborazione non riuscita. Riprova o torna più tardi.',
  'recordingEditor.failed.overlay': 'Impossibile elaborare',
  'recordingEditor.failed.title': 'Elaborazione non riuscita',
  'recordingEditor.failed.tryAgain': 'Riprova',
  'recordingEditor.header.back': 'Torna alle registrazioni',
  'recordingEditor.header.download': 'Scarica',
  'recordingEditor.header.downloading': 'Download in corso...',
  'recordingEditor.header.viewPlans': 'Visualizza i piani',
  'recordingEditor.leaveHint': 'Puoi lasciare questa pagina e tornare più tardi.',
  'recordingEditor.mobileBanner.dismiss': 'Chiudi',
  'recordingEditor.mobileBanner.text': "Per un'esperienza di modifica migliore, aprilo da computer.",
  'recordingEditor.notFound.description': 'La pagina che cerchi non esiste.',
  'recordingEditor.notFound.goHome': 'Vai alla home',
  'recordingEditor.notFound.title': 'Pagina non trovata',
  'recordingEditor.player.back': 'Indietro di 10 secondi',
  'recordingEditor.player.backTooltip': 'Indietro di 10 s',
  'recordingEditor.player.forward': 'Avanti di 10 secondi',
  'recordingEditor.player.forwardTooltip': 'Avanti di 10 s',
  'recordingEditor.player.pause': 'Pausa',
  'recordingEditor.player.play': 'Riproduci',
  'recordingEditor.player.playPauseTooltip': 'Riproduci / Pausa',
  'recordingEditor.player.seek': 'Posizione di riproduzione',
  'recordingEditor.player.spaceKey': 'Spazio',
  'recordingEditor.player.speed': 'Regola la velocità di riproduzione',
  'recordingEditor.player.speedTooltip': 'Regola la velocità',
  'recordingEditor.player.translate': 'Traduci',
  'recordingEditor.player.translateTooltip': 'Localizza questo video nell’editor di traduzione',
  'recordingEditor.preview.description': 'Anteprima del tuo video rifinito. Il rendering finale è in corso.',
  'recordingEditor.preview.preparing': 'Preparazione dell’anteprima...',
  'recordingEditor.processing.title': 'Elaborazione del video',
  'recordingEditor.rendering.description':
    'Stiamo applicando le tue ultime modifiche. Nel frattempo puoi continuare a guardare il video qui sotto: tieni solo aperta questa scheda.',
  'recordingEditor.rendering.downloadStarted': 'Download avviato',
  'recordingEditor.rendering.locked': 'Modifiche bloccate durante il rendering',
  'recordingEditor.rendering.shareDescription':
    'Il link di condivisione si aprirà automaticamente quando il video sarà pronto. Puoi continuare a guardarlo qui sotto, ma non chiudere questa scheda.',
  'recordingEditor.rendering.title': 'Stiamo preparando il video…',
  'recordingEditor.timeline.autoPauseOff': 'Disattiva il taglio automatico della pausa che inizia a {time}',
  'recordingEditor.timeline.autoPauseOn': 'Attiva il taglio automatico della pausa che inizia a {time}',
  'recordingEditor.timeline.autoPauseRemove': 'Rimuovi il taglio automatico della pausa a {time}',
  'recordingEditor.timeline.autoPauseRestore': 'Ripristina il taglio automatico della pausa a {time}',
  'recordingEditor.timeline.frameAlt': 'Fotogramma a {time}',
  'recordingEditor.timeline.moveZoom': 'Sposta lo zoom che inizia a {time}',
  'recordingEditor.timeline.removed': 'Rimosso',
  'recordingEditor.timeline.zoomChip': 'zoom',
  'recordingEditor.timeline.zoomIn': 'Ingrandisci la timeline',
  'recordingEditor.timeline.zoomOut': 'Riduci la timeline',
  'recordingEditor.timeline.zoomSlider': 'Zoom della timeline',
  'recordingEditor.transcript.cutHint': 'Parola rimossa dal video. Fai clic per annullare.',
  'recordingEditor.transcript.emptyDescription':
    "Non c'è niente da tagliare, ma puoi comunque vedere l'anteprima, condividere o scaricare la registrazione.",
  'recordingEditor.transcript.emptyTitle': 'Nessun parlato rilevato',
  'recordingEditor.transcript.hint': 'Tocca le parole per rimuoverle dal video o ripristinarle.',
  'recordingEditor.transcript.tabTooltip': 'Modifica il video modificandone la trascrizione.',
  'recordingEditor.transcript.title': 'Trascrizione',
} satisfies Translation;

const ru = {
  'recordingEditor.aborted.description': 'Запись была прервана, и её нельзя обработать.',
  'recordingEditor.aborted.title': 'Запись прервана',
  'recordingEditor.analyzing.title': 'Анализируем вашу речь...',
  'recordingEditor.autoZoom.cardTitle': 'Зум {number}',
  'recordingEditor.autoZoom.emptyDescription': 'Мы не нашли в этой записи моментов, которые стоило бы приблизить.',
  'recordingEditor.autoZoom.emptyTitle': 'Зумов нет',
  'recordingEditor.autoZoom.locked':
    'Приближает область, на которую вы нажимаете, чтобы зрителям было проще следить за происходящим.',
  'recordingEditor.autoZoom.masterSwitch': 'Включить или выключить все автозумы',
  'recordingEditor.autoZoom.preset.medium': 'Средний',
  'recordingEditor.autoZoom.preset.strong': 'Сильный',
  'recordingEditor.autoZoom.preset.subtle': 'Слабый',
  'recordingEditor.autoZoom.title': 'Автозум',
  'recordingEditor.autoZoom.turnOff': 'Выключить зум, начинающийся в {time}',
  'recordingEditor.autoZoom.turnOn': 'Включить зум, начинающийся в {time}',
  'recordingEditor.autoZoom.zoomSwitchTooltip': 'Включить / выключить этот зум',
  'recordingEditor.defaultTitle': 'Запись',
  'recordingEditor.failed.description': 'Не удалось обработать запись. Попробуйте ещё раз или вернитесь позже.',
  'recordingEditor.failed.overlay': 'Не удалось обработать',
  'recordingEditor.failed.title': 'Ошибка обработки',
  'recordingEditor.failed.tryAgain': 'Попробовать снова',
  'recordingEditor.header.back': 'Назад к записям',
  'recordingEditor.header.download': 'Скачать',
  'recordingEditor.header.downloading': 'Скачивание...',
  'recordingEditor.header.viewPlans': 'Посмотреть тарифы',
  'recordingEditor.leaveHint': 'Можете закрыть эту страницу и вернуться позже.',
  'recordingEditor.mobileBanner.dismiss': 'Закрыть',
  'recordingEditor.mobileBanner.text': 'Для удобного редактирования откройте запись на компьютере.',
  'recordingEditor.notFound.description': 'Страница, которую вы ищете, не существует.',
  'recordingEditor.notFound.goHome': 'На главную',
  'recordingEditor.notFound.title': 'Страница не найдена',
  'recordingEditor.player.back': 'Назад на 10 секунд',
  'recordingEditor.player.backTooltip': 'Назад на 10 с',
  'recordingEditor.player.forward': 'Вперёд на 10 секунд',
  'recordingEditor.player.forwardTooltip': 'Вперёд на 10 с',
  'recordingEditor.player.pause': 'Пауза',
  'recordingEditor.player.play': 'Воспроизвести',
  'recordingEditor.player.playPauseTooltip': 'Воспроизведение / пауза',
  'recordingEditor.player.seek': 'Позиция воспроизведения',
  'recordingEditor.player.spaceKey': 'Пробел',
  'recordingEditor.player.speed': 'Изменить скорость воспроизведения',
  'recordingEditor.player.speedTooltip': 'Изменить скорость',
  'recordingEditor.player.translate': 'Перевести',
  'recordingEditor.player.translateTooltip': 'Локализовать видео в редакторе перевода',
  'recordingEditor.preview.description': 'Предпросмотр обработанного видео. Идёт финальный рендеринг.',
  'recordingEditor.preview.preparing': 'Готовим предпросмотр...',
  'recordingEditor.processing.title': 'Обработка видео',
  'recordingEditor.rendering.description':
    'Применяем ваши последние правки. А пока можно продолжать смотреть видео ниже — просто не закрывайте эту вкладку.',
  'recordingEditor.rendering.downloadStarted': 'Скачивание началось',
  'recordingEditor.rendering.locked': 'Редактирование недоступно во время рендеринга',
  'recordingEditor.rendering.shareDescription':
    'Ссылка для просмотра откроется автоматически, когда видео будет готово. Можно продолжать смотреть ниже — только не закрывайте эту вкладку.',
  'recordingEditor.rendering.title': 'Готовим видео…',
  'recordingEditor.timeline.autoPauseOff': 'Отключить автоматическую вырезку паузы в {time}',
  'recordingEditor.timeline.autoPauseOn': 'Включить автоматическую вырезку паузы в {time}',
  'recordingEditor.timeline.autoPauseRemove': 'Убрать автоматическую вырезку паузы в {time}',
  'recordingEditor.timeline.autoPauseRestore': 'Вернуть автоматическую вырезку паузы в {time}',
  'recordingEditor.timeline.frameAlt': 'Кадр в {time}',
  'recordingEditor.timeline.moveZoom': 'Переместить зум, начинающийся в {time}',
  'recordingEditor.timeline.removed': 'Удалено',
  'recordingEditor.timeline.zoomChip': 'зум',
  'recordingEditor.timeline.zoomIn': 'Увеличить масштаб таймлайна',
  'recordingEditor.timeline.zoomOut': 'Уменьшить масштаб таймлайна',
  'recordingEditor.timeline.zoomSlider': 'Масштаб таймлайна',
  'recordingEditor.transcript.cutHint': 'Слово удалено из видео. Нажмите на него, чтобы вернуть.',
  'recordingEditor.transcript.emptyDescription':
    'Вырезать здесь нечего, но запись всё равно можно посмотреть, отправить по ссылке или скачать.',
  'recordingEditor.transcript.emptyTitle': 'Речь не обнаружена',
  'recordingEditor.transcript.hint': 'Нажимайте на слова, чтобы убрать их из видео или вернуть.',
  'recordingEditor.transcript.tabTooltip': 'Редактируйте видео через его транскрипцию.',
  'recordingEditor.transcript.title': 'Транскрипция',
} satisfies Translation;

export const RECORDING_EDITOR_TRANSLATIONS = defineDomainTranslations({ en, es, fr, it, pt, ru });
